import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Download, Loader2, Check, AlertCircle, Film } from 'lucide-react'
import { Button, Card } from '@trafiflow/shared-ui'
import JSZip from 'jszip'
import { StoryboardComparison } from './StoryboardComparison'
import { StoryboardHeader } from './StoryboardHeader'
import { FootageSelectionModal, getCategoryForSceneLabel } from './modals/FootageSelectionModal'
import {
  getBenchmarkFrames,
  getSourceFrames,
  mockBriefData,
  mockFootageRepository,
  mockStoryboardSettings,
} from './mock'
import type { FootageData, StoryboardFrameData, StoryboardSettings } from './types'
import { useStoryboard, useUpdateStoryboard, useCreateStoryboard } from './hooks/useStoryboards'
import type { StoryboardBrief, StoryboardSettingsData } from './storyboards.service'
import {
  assembleStoryboard,
  getAssemblyJobStatus,
  type StoryboardAssemblyResponse,
} from './storyboards.service'
import {
  listCompanyVideos,
  getCompanyVideo,
  type CompanyVideoWithThumbnail,
} from '../../repository/companyVideos.service'
import { useBrandContext } from '../../../hooks/useBrandContext'
import { agentplatService } from '../../../../../services/agentplat'
import { DEFAULT_FALLBACK_AGENT_ID } from '../../chats/chatPresets'

interface LocationState {
  sourceFrames?: StoryboardFrameData[]
  generatedFrames?: StoryboardFrameData[]
  meta?: {
    durationSec?: number
    scenesCount?: number
    suggestedHooks?: string[]
    brand?: string
    product?: string
    model?: string
  }
  jobId?: string
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

function mapBriefDataToApi(brief: typeof mockBriefData): StoryboardBrief {
  return {
    productName: brief.productName,
    targetAudience: brief.targetAudience,
    mainBenefit: brief.mainBenefit,
    painPoints: brief.painPoints,
    tone: brief.tone,
    callToAction: brief.callToAction,
    keyMessages: brief.keyMessages,
    videoLength: brief.videoLength,
    platform: brief.platform,
  }
}

function mapSettingsToApi(settings: StoryboardSettings): StoryboardSettingsData {
  return {
    name: settings.name,
    aspectRatio: settings.aspectRatio,
    duration: settings.duration,
    targetPlatform: settings.targetPlatform,
    style: settings.style,
    audioTrack: settings.audioTrack,
    language: settings.language,
  }
}

function compressDataUrl(dataUrl: string, maxWidth = 80, quality = 0.3): Promise<string> {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width)
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      if (!ctx) { resolve(''); return }
      ctx.drawImage(img, 0, 0, w, h)
      resolve(canvas.toDataURL('image/jpeg', quality))
    }
    img.onerror = () => resolve('')
    img.src = dataUrl
  })
}

// Parse time string "M:SS" to seconds
function parseTimeToSeconds(timeStr: string): number {
  const parts = timeStr.split(':')
  if (parts.length === 2) {
    return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
  }
  return 0
}

interface VideoScoreResult {
  video: CompanyVideoWithThumbnail
  score: number
}

function scoreVideoForScene(
  video: CompanyVideoWithThumbnail,
  label: string,
  description?: string,
  textOverlay?: string,
  audioText?: string
): number {
  let score = 0

  const category = getCategoryForSceneLabel(label)
  const labelLower = label.toLowerCase()
  const descLower = (description || '').toLowerCase()
  const textLower = (textOverlay || '').toLowerCase()
  const audioLower = (audioText || '').toLowerCase()
  const allContent = `${labelLower} ${descLower} ${textLower} ${audioLower}`

  // Category match (30 points)
  if (category) {
    if (video.category?.toLowerCase() === category) {
      score += 30
    }
    if (video.contentRole?.toLowerCase() === category) {
      score += 25
    }
  }

  // Tag matching (20 points max)
  if (video.tags && video.tags.length > 0) {
    let tagScore = 0
    for (const tag of video.tags) {
      const tagLower = tag.toLowerCase()
      if (allContent.includes(tagLower) || tagLower.includes(labelLower)) {
        tagScore += 5
      }
    }
    score += Math.min(tagScore, 20)
  }

  // Description/AI analysis matching (25 points max)
  const videoDesc = (video.aiDescription || video.description || '').toLowerCase()
  if (videoDesc && allContent.length > 0) {
    const contentWords = allContent.split(/\s+/).filter(w => w.length > 3)
    let descMatches = 0
    for (const word of contentWords) {
      if (videoDesc.includes(word)) {
        descMatches++
      }
    }
    if (descMatches > 0) {
      score += Math.min(descMatches * 3, 25)
    }
  }

  return score
}

function findBestMatch(
  frame: { label: string; description?: string; textOverlay?: string; audioText?: string },
  videos: CompanyVideoWithThumbnail[],
  usedIds: Set<string>,
  allowFallback: boolean = true
): CompanyVideoWithThumbnail | null {
  // Score all unused videos and pick the best match
  const candidates: VideoScoreResult[] = []
  const unusedVideos: CompanyVideoWithThumbnail[] = []
  const allScores: Array<{ videoId: string; title: string; score: number }> = []

  for (const video of videos) {
    if (usedIds.has(video.id)) continue
    unusedVideos.push(video)

    const score = scoreVideoForScene(
      video,
      frame.label,
      frame.description,
      frame.textOverlay,
      frame.audioText
    )

    allScores.push({ videoId: video.id, title: video.title || video.fileName, score })

    // Lower threshold for generic labels like "Scene"
    const isGenericLabel = frame.label.toLowerCase() === 'scene' ||
                           frame.label.toLowerCase() === 'transition'
    const minScore = isGenericLabel ? 5 : 15

    if (score >= minScore) {
      candidates.push({ video, score })
    }
  }

  console.log('[findBestMatch] Frame:', frame.label, '| Scores:', allScores, '| Candidates:', candidates.length, '| Unused:', unusedVideos.length)

  if (candidates.length > 0) {
    // Sort by score descending and return the best match
    candidates.sort((a, b) => b.score - a.score)
    console.log('[findBestMatch] Best match:', candidates[0].video.title, 'score:', candidates[0].score)
    return candidates[0].video
  }

  // Fallback: if no scored match found but we allow fallback, pick first unused video
  // This ensures scenes get SOME video assigned rather than being placeholders
  if (allowFallback && unusedVideos.length > 0) {
    console.log('[findBestMatch] Using fallback for frame:', frame.label, '→', unusedVideos[0].title || unusedVideos[0].fileName)
    return unusedVideos[0]
  }

  console.log('[findBestMatch] No match found for frame:', frame.label)
  return null
}

export function StoryboardDetail() {
  const { storyboardId } = useParams<{ storyboardId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const locationState = location.state as LocationState | null
  const copyResetRef = useRef<number | null>(null)
  const hasInitializedFromState = useRef(false)
  const autoSaveTimeoutRef = useRef<number | null>(null)

  const isNewStoryboard = !storyboardId || storyboardId === 'new'
  const hasGeneratedFrames = locationState?.generatedFrames && locationState.generatedFrames.length > 0

  // API hooks
  const { data: apiStoryboard, isLoading: isLoadingStoryboard } = useStoryboard(
    isNewStoryboard ? undefined : storyboardId
  )
  const updateMutation = useUpdateStoryboard()
  const createMutation = useCreateStoryboard()

  // Save status
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [persistedId, setPersistedId] = useState<string | null>(
    isNewStoryboard ? null : storyboardId ?? null
  )

  const initialName = useMemo(() => {
    if (apiStoryboard?.name) {
      return apiStoryboard.name
    }
    if (locationState?.meta?.product) {
      return `${locationState.meta.product} Storyboard`
    }
    if (locationState?.meta?.brand) {
      return `${locationState.meta.brand} Storyboard`
    }
    if (!storyboardId || storyboardId === 'new') return 'New Storyboard'
    if (/^\d+$/.test(storyboardId)) return `Storyboard ${storyboardId}`
    return storyboardId.replace(/[-_]/g, ' ')
  }, [storyboardId, locationState?.meta?.product, locationState?.meta?.brand, apiStoryboard?.name])

  const [storyboardName, setStoryboardName] = useState(initialName)
  const hasLocationData = !!(locationState?.sourceFrames?.length || hasGeneratedFrames)

  const [sourceFrames, setSourceFrames] = useState<StoryboardFrameData[]>(() => {
    if (locationState?.sourceFrames && locationState.sourceFrames.length > 0) {
      return locationState.sourceFrames
    }
    if (!isNewStoryboard) return []
    return getSourceFrames()
  })
  const [benchmarkFrames, setBenchmarkFrames] = useState<StoryboardFrameData[]>(() => {
    if (hasGeneratedFrames) {
      return locationState!.generatedFrames!
    }
    if (!isNewStoryboard) return []
    return getBenchmarkFrames(1)
  })
  const [settings] = useState<StoryboardSettings>(mockStoryboardSettings)
  const [selectedFrame, setSelectedFrame] = useState<StoryboardFrameData | null>(null)
  const [isFootageModalOpen, setIsFootageModalOpen] = useState(false)
  const [footageModalInitialTab, setFootageModalInitialTab] = useState<'suggested' | 'preview'>('suggested')
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const hasLoadedFromApi = useRef(false)
  const autoFillDoneRef = useRef(false)
  const copyAdaptedRef = useRef(false)
  const [isAdaptingCopy, setIsAdaptingCopy] = useState(false)

  // Export state
  const [isExporting, setIsExporting] = useState(false)
  const [exportingStatus, setExportingStatus] = useState<string>('Exporting...')
  const [showMissingClipsWarning, setShowMissingClipsWarning] = useState(false)
  const [showExportResult, setShowExportResult] = useState(false)
  const [exportResult, setExportResult] = useState<StoryboardAssemblyResponse | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)
  const [isExportingClips, setIsExportingClips] = useState(false)

  const { brandContext } = useBrandContext()

  const { data: repositoryVideos } = useQuery({
    queryKey: ['company-videos', 'storyboard-auto-fill'],
    queryFn: () => listCompanyVideos({ limit: 50 }),
    staleTime: 60000,
  })

  const markAsChanged = useCallback(() => {
    setHasUnsavedChanges(true)
    setSaveStatus('idle')
  }, [])

  // Load storyboard from API — always applies when data arrives (unless location state was used)
  useEffect(() => {
    if (!apiStoryboard || hasLoadedFromApi.current) return
    // If we already have location-state frames, skip overwriting
    if (hasLocationData) {
      hasLoadedFromApi.current = true
      hasInitializedFromState.current = true
      return
    }

    setStoryboardName(apiStoryboard.name)
    if (apiStoryboard.sourceFrames && apiStoryboard.sourceFrames.length > 0) {
      setSourceFrames(apiStoryboard.sourceFrames)
    }
    if (apiStoryboard.frames && apiStoryboard.frames.length > 0) {
      setBenchmarkFrames(apiStoryboard.frames)
    }
    hasLoadedFromApi.current = true
    hasInitializedFromState.current = true
  }, [apiStoryboard, hasLocationData])

  // Update name when initial name changes (from API load)
  useEffect(() => {
    if (!hasInitializedFromState.current) {
      setStoryboardName(initialName)
    }
  }, [initialName])

  // Auto-fill benchmark frames that don't have a company video assigned (sourceVideoId).
  // This runs for:
  //   - Freshly generated storyboards from AI
  //   - API-loaded storyboards where frames have thumbnails but no sourceVideoId
  // The key is: frames need sourceVideoId set for export to work properly.
  useEffect(() => {
    console.log('[Auto-fill] Effect triggered', {
      autoFillDone: autoFillDoneRef.current,
      videosCount: repositoryVideos?.videos?.length ?? 0,
      benchmarkFramesCount: benchmarkFrames.length,
    })

    if (autoFillDoneRef.current) {
      console.log('[Auto-fill] Skipping: already done')
      return
    }
    const videos = repositoryVideos?.videos
    if (!videos || videos.length === 0) {
      console.log('[Auto-fill] Skipping: no videos in repository')
      return
    }
    if (benchmarkFrames.length === 0) {
      console.log('[Auto-fill] Skipping: no benchmark frames')
      return
    }

    // Find frames that need a company video assigned (missing sourceVideoId)
    const framesNeedingVideo = benchmarkFrames.filter(f => !f.sourceVideoId)
    if (framesNeedingVideo.length === 0) {
      console.log('[Auto-fill] All frames already have sourceVideoId')
      autoFillDoneRef.current = true
      return
    }

    console.log('[Auto-fill] Frames needing video assignment:', framesNeedingVideo.length, 'of', benchmarkFrames.length)
    console.log('[Auto-fill] Available videos:', videos.map(v => ({ id: v.id, title: v.title, contentRole: v.contentRole })))

    autoFillDoneRef.current = true
    const usedIds = new Set<string>()
    benchmarkFrames.forEach(f => { if (f.sourceVideoId) usedIds.add(f.sourceVideoId) })

    let hasChanges = false
    const updated = benchmarkFrames.map(frame => {
      // Skip frames that already have a company video assigned
      if (frame.sourceVideoId) return frame

      const match = findBestMatch(frame, videos, usedIds)
      if (!match) {
        console.log('[Auto-fill] No match found for frame:', frame.label, frame.id)
        return frame
      }

      console.log('[Auto-fill] Matched frame:', frame.label, '→', match.title || match.fileName, '(', match.id, ')')

      usedIds.add(match.id)
      hasChanges = true

      // Calculate scene duration from timeStart/timeEnd
      const sceneStartSec = parseTimeToSeconds(frame.timeStart)
      const sceneEndSec = parseTimeToSeconds(frame.timeEnd)
      const sceneDuration = sceneEndSec - sceneStartSec

      // Auto-suggest clip trim: start from 0, end at scene duration or video duration (whichever is smaller)
      const videoDuration = match.durationSec || 0
      const clipStartSec = 0
      const clipEndSec = videoDuration > 0 ? Math.min(sceneDuration, videoDuration) : sceneDuration

      return {
        ...frame,
        // Only update thumbnail if frame doesn't have one, or use company video thumbnail
        image: frame.image || match.thumbnailUrl || '',
        sourceVideoId: match.id,
        // Keep existing description if present, otherwise use match description
        description: frame.description || match.aiDescription || match.description,
        clipStartSec,
        clipEndSec: clipEndSec > 0 ? clipEndSec : undefined,
      }
    })

    if (hasChanges) {
      console.log('[Auto-fill] Updated', updated.filter(f => f.sourceVideoId).length, 'frames with sourceVideoId')
      setBenchmarkFrames(updated)
      markAsChanged()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repositoryVideos, benchmarkFrames, markAsChanged])

  // Generate NEW brand-specific copy for benchmark frames using AI.
  // This runs when:
  //   (a) it's a fresh storyboard (from generator) with empty benchmark copy, OR
  //   (b) benchmark text needs adaptation based on brand context
  // Uses SOURCE frames as reference for the ad structure/angle, but generates NEW copy.
  useEffect(() => {
    console.log('[CopyAdaptation] Effect triggered', {
      copyAdaptedRef: copyAdaptedRef.current,
      isAdaptingCopy,
      brandContext: brandContext?.substring(0, 50),
      benchmarkFramesCount: benchmarkFrames.length,
      sourceFramesCount: sourceFrames.length,
      hasGeneratedFrames,
    })

    if (copyAdaptedRef.current || isAdaptingCopy) {
      console.log('[CopyAdaptation] Skipping: already adapted or adapting')
      return
    }
    if (!brandContext?.trim()) {
      console.log('[CopyAdaptation] Skipping: no brand context available')
      return
    }
    if (benchmarkFrames.length === 0) {
      console.log('[CopyAdaptation] Skipping: no benchmark frames')
      return
    }

    // Check if benchmark frames need copy generation (empty or never adapted)
    const benchmarkHasText = benchmarkFrames.some(f => f.textOverlay || f.audioText)
    const sourceHasText = sourceFrames.some(f => f.textOverlay || f.audioText)

    console.log('[CopyAdaptation] Text check', { benchmarkHasText, sourceHasText, hasGeneratedFrames })

    // Skip if benchmark already has custom copy (not from source)
    if (benchmarkHasText && !hasGeneratedFrames) {
      // Check if it's just copied from source
      const copyIsVerbatimSource = sourceFrames.length > 0 &&
        benchmarkFrames.every((bf, i) => {
          const sf = sourceFrames[i]
          if (!sf) return false
          return bf.textOverlay === sf.textOverlay && bf.audioText === sf.audioText
        })
      console.log('[CopyAdaptation] Verbatim source check', { copyIsVerbatimSource })
      if (!copyIsVerbatimSource) {
        console.log('[CopyAdaptation] Skipping: benchmark has custom copy')
        return
      }
    }

    // If neither benchmark nor source has text, nothing to generate from
    if (!sourceHasText && !benchmarkHasText) {
      console.log('[CopyAdaptation] Skipping: no text in source or benchmark')
      return
    }

    console.log('[CopyAdaptation] Starting AI adaptation with brand context:', brandContext.substring(0, 100))
    copyAdaptedRef.current = true
    setIsAdaptingCopy(true)

    // Use SOURCE frames as reference for the ad structure/angle
    // AI will generate NEW copy based on scene labels and source descriptions
    const scenes = benchmarkFrames.map((f, i) => {
      const sourceFrame = sourceFrames[i]
      return {
        scene: i + 1,
        label: f.label,
        description: f.description || sourceFrame?.description || '',
        // Provide source copy as REFERENCE for angle/idea, not to copy
        sourceTextOverlay: sourceFrame?.textOverlay || '',
        sourceAudio: sourceFrame?.audioText || '',
        time: `${f.timeStart} - ${f.timeEnd}`,
      }
    })

    const prompt = [
      `[TASK]\nYou are adapting a SOURCE VIDEO's ad copy for a new brand. Your goal is to PRESERVE the source video's ANGLE, STRUCTURE, and MESSAGING APPROACH while adapting it for the new brand.`,
      ``,
      `[SOURCE VIDEO - REFERENCE COPY]`,
      `Analyze this source video's approach:`,
      `${scenes.map(s => `- Scene ${s.scene} (${s.label}): Text="${s.sourceTextOverlay || '—'}" | Audio="${s.sourceAudio || '—'}"`).join('\n')}`,
      ``,
      `[NEW BRAND CONTEXT]`,
      `${brandContext.trim()}`,
      ``,
      `[INSTRUCTIONS]`,
      `Adapt the source video's copy for the new brand by:`,
      ``,
      `1. PRESERVE THE ANGLE: If the source is a product demo, yours is a product demo. If it's a problem/solution pitch, yours is too. If it's a casual unboxing, yours is casual unboxing.`,
      ``,
      `2. PRESERVE THE TONE: Match the source's energy and style (excited, calm, professional, friendly, skeptical, etc.)`,
      ``,
      `3. PRESERVE THE STRUCTURE: Follow the same flow (intro → features → benefits → CTA, or hook → problem → solution, etc.)`,
      ``,
      `4. ADAPT ONLY: Brand name, product name, and specific features to match the new brand's offerings`,
      ``,
      `CRITICAL: Do NOT invent a new angle. Your copy must follow the source video's messaging approach.`,
      `- Source shows product features → You show product features`,
      `- Source talks about a problem → You talk about the equivalent problem`,
      `- Source does a casual intro → You do a casual intro`,
      `- Source lists benefits → You list benefits`,
      ``,
      `Rules:`,
      `- Text overlay: SHORT, punchy, max 10-15 words, often ALL CAPS`,
      `- Audio: Natural voiceover script matching the source length, format as "Voiceover: '...'"`,
      `- If source scene has no text overlay, you can leave it empty or add minimal text`,
      `- Return ONLY valid JSON array, no markdown fences`,
      ``,
      `Scenes:\n${JSON.stringify(scenes, null, 2)}`,
      ``,
      `Return:\n[{"scene":1,"textOverlay":"...","audioText":"Voiceover: '...'"},...]`,
    ].join('\n')

    agentplatService.invokeAgent(DEFAULT_FALLBACK_AGENT_ID, prompt, undefined, true)
      .then(result => {
        try {
          const raw = result.content.trim()
          const jsonMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/)
          const jsonStr = jsonMatch ? jsonMatch[1].trim() : raw
          const parsed: Array<{ scene: number; textOverlay: string; audioText: string }> = JSON.parse(jsonStr)

          if (Array.isArray(parsed) && parsed.length > 0) {
            setBenchmarkFrames(prev => prev.map((frame, i) => {
              const adapted = parsed.find(p => p.scene === i + 1)
              if (!adapted) return frame
              return {
                ...frame,
                textOverlay: adapted.textOverlay || frame.textOverlay,
                audioText: adapted.audioText || frame.audioText,
              }
            }))
            markAsChanged()
          }
        } catch (parseErr) {
          console.warn('[StoryboardDetail] Failed to parse adapted copy:', parseErr)
        }
      })
      .catch(err => {
        console.warn('[StoryboardDetail] Copy adaptation failed:', err)
      })
      .finally(() => {
        setIsAdaptingCopy(false)
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandContext, benchmarkFrames, sourceFrames, hasGeneratedFrames, isAdaptingCopy, markAsChanged])

  useEffect(() => {
    return () => {
      if (copyResetRef.current) {
        window.clearTimeout(copyResetRef.current)
      }
      if (autoSaveTimeoutRef.current) {
        window.clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [])

  // Auto-save effect
  const performSave = useCallback(async () => {
    if (!hasUnsavedChanges) return

    setSaveStatus('saving')

    try {
      const compressedSourceFrames = await Promise.all(
        sourceFrames.map(async f => ({
          ...f,
          image: f.image?.startsWith('data:')
            ? await compressDataUrl(f.image, 100, 0.4)
            : f.image,
        }))
      )

      const persistableBenchmarkFrames = benchmarkFrames.map(f => ({
        ...f,
        image: f.image?.startsWith('data:') ? '' : f.image,
      }))

      // Log frames being saved to verify sourceVideoId is included
      console.log('[performSave] Saving benchmark frames:', persistableBenchmarkFrames.map(f => ({
        id: f.id,
        label: f.label,
        sourceVideoId: f.sourceVideoId,
        hasImage: !!f.image,
        clipStartSec: f.clipStartSec,
        clipEndSec: f.clipEndSec,
      })))

      const payload = {
        name: storyboardName,
        sourceFrames: compressedSourceFrames,
        frames: persistableBenchmarkFrames,
        settings: mapSettingsToApi(settings),
        brief: mapBriefDataToApi(mockBriefData),
        geminiJobId: locationState?.jobId,
      }

      if (persistedId) {
        // Update existing
        await updateMutation.mutateAsync({ id: persistedId, ...payload })
      } else {
        // Create new
        const created = await createMutation.mutateAsync(payload)
        setPersistedId(created.id)
        // Update URL to reflect the new ID (without reloading)
        navigate(`/marketing/playground/storyboard/${created.id}`, { replace: true })
      }

      setSaveStatus('saved')
      setHasUnsavedChanges(false)

      // Reset status after 2 seconds
      setTimeout(() => {
        setSaveStatus('idle')
      }, 2000)
    } catch (error) {
      console.error('Failed to save storyboard:', error)
      setSaveStatus('error')
    }
  }, [
    hasUnsavedChanges,
    storyboardName,
    sourceFrames,
    benchmarkFrames,
    settings,
    persistedId,
    updateMutation,
    createMutation,
    locationState?.jobId,
    navigate,
  ])

  // Debounced auto-save
  useEffect(() => {
    if (!hasUnsavedChanges) return

    if (autoSaveTimeoutRef.current) {
      window.clearTimeout(autoSaveTimeoutRef.current)
    }

    autoSaveTimeoutRef.current = window.setTimeout(() => {
      performSave()
    }, 2000)

    return () => {
      if (autoSaveTimeoutRef.current) {
        window.clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [hasUnsavedChanges, performSave])

  const handleStoryboardNameChange = useCallback((name: string) => {
    setStoryboardName(name)
    markAsChanged()
  }, [markAsChanged])

  const handleRegenerate = () => {
    markAsChanged()
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard?.writeText(window.location.href)
      setCopyStatus('success')
    } catch (error) {
      console.error('Failed to copy link', error)
      setCopyStatus('error')
    }

    if (copyResetRef.current) {
      window.clearTimeout(copyResetRef.current)
    }
    copyResetRef.current = window.setTimeout(() => {
      setCopyStatus('idle')
    }, 2000)
  }

  const handleBenchmarkFrameClick = (frame: StoryboardFrameData) => {
    setSelectedFrame(frame)
    setFootageModalInitialTab('suggested')
    setIsFootageModalOpen(true)
  }

  const handleBenchmarkFramePreview = (frame: StoryboardFrameData) => {
    setSelectedFrame(frame)
    setFootageModalInitialTab('preview')
    setIsFootageModalOpen(true)
  }

  const handleFootageConfirm = (selectedFootage: FootageData, clipStartSec?: number, clipEndSec?: number) => {
    if (!selectedFrame) {
      console.warn('[handleFootageConfirm] No selectedFrame')
      return
    }

    console.log('[handleFootageConfirm] Setting footage:', {
      selectedFrameId: selectedFrame.id,
      selectedFrameLabel: selectedFrame.label,
      footageId: selectedFootage.id,
      footageThumbnail: selectedFootage.thumbnail?.slice(0, 50),
    })

    setBenchmarkFrames(prev => {
      const updated = prev.map(frame =>
        frame.id === selectedFrame.id
          ? {
              ...frame,
              image: selectedFootage.thumbnail,
              videoUrl: undefined,
              sourceVideoId: selectedFootage.id,
              description: selectedFootage.description ?? frame.description,
              clipStartSec,
              clipEndSec,
            }
          : frame
      )

      // Log what was updated
      const updatedFrame = updated.find(f => f.id === selectedFrame.id)
      console.log('[handleFootageConfirm] Updated frame:', {
        frameId: updatedFrame?.id,
        sourceVideoId: updatedFrame?.sourceVideoId,
        hasImage: !!updatedFrame?.image,
      })

      return updated
    })
    markAsChanged()
  }

  const handleBenchmarkRegenerate = (frame: StoryboardFrameData) => {
    const nextFootage =
      mockFootageRepository[Math.floor(Math.random() * mockFootageRepository.length)]
    setBenchmarkFrames(prev =>
      prev.map(item =>
        item.id === frame.id
          ? { ...item, image: nextFootage.thumbnail, description: item.description }
          : item
      )
    )
    markAsChanged()
  }

  const handleBenchmarkRemove = (frame: StoryboardFrameData) => {
    setBenchmarkFrames(prev => prev.filter(f => f.id !== frame.id))
    markAsChanged()
  }

  const handleBenchmarkUpdate = (frameId: string, updates: Partial<StoryboardFrameData>) => {
    setBenchmarkFrames(prev =>
      prev.map(frame =>
        frame.id === frameId ? { ...frame, ...updates } : frame
      )
    )
    markAsChanged()
  }

  const handleRegenerateSection = (section: 'hook' | 'all') => {
    if (section === 'hook') {
      setBenchmarkFrames(prev =>
        prev.map(frame =>
          frame.label === 'Hook'
            ? {
                ...frame,
                image:
                  mockFootageRepository[
                    Math.floor(Math.random() * mockFootageRepository.length)
                  ].thumbnail,
              }
            : frame
        )
      )
    }
    markAsChanged()
  }

  // Calculate missing clips for export warning
  // A clip is "missing" only if it has no sourceVideoId assigned
  // (image/thumbnail doesn't matter - we need the actual video ID for export)
  const missingClipsCount = useMemo(() => {
    return benchmarkFrames.filter(f => !f.sourceVideoId).length
  }, [benchmarkFrames])

  const handleExportVideoClick = () => {
    if (missingClipsCount > 0) {
      setShowMissingClipsWarning(true)
    } else {
      performExport()
    }
  }

  const performExport = async () => {
    setShowMissingClipsWarning(false)
    setIsExporting(true)
    setExportingStatus('Queuing export...')
    setExportError(null)

    try {
      // Parse time string "M:SS" to seconds
      const parseTime = (timeStr: string): number => {
        const parts = timeStr.split(':')
        if (parts.length === 2) {
          return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
        }
        return 0
      }

      console.log('[performExport] Benchmark frames:', benchmarkFrames.map(f => ({
        id: f.id,
        label: f.label,
        sourceVideoId: f.sourceVideoId,
        hasImage: !!f.image,
      })))

      // Build scenes array from benchmark frames
      const scenes = benchmarkFrames.map((frame, index) => ({
        sceneNumber: index + 1,
        startTime: parseTime(frame.timeStart),
        endTime: parseTime(frame.timeEnd),
        description: frame.description || '',
        contentRole: frame.label.toLowerCase().replace(/\s+/g, '_'),
        visualType: frame.visualTreatment || 'scene',
        audio: frame.audioText || '',
        textOverlay: frame.textOverlay || '',
        mood: 'neutral',
        keyElements: [],
        // Include video ID if available for direct matching
        ...(frame.sourceVideoId && { videoId: frame.sourceVideoId }),
        // Include clip trim times if set
        ...(frame.clipStartSec !== undefined && { clipStartSec: frame.clipStartSec }),
        ...(frame.clipEndSec !== undefined && { clipEndSec: frame.clipEndSec }),
      }))

      // Log scenes being sent to API - DETAILED
      console.log('[performExport] Scenes to export (full):')
      console.log(JSON.stringify(scenes, null, 2))

      // Calculate total duration
      const totalDuration = scenes.reduce((sum, scene) => {
        return sum + (scene.endTime - scene.startTime)
      }, 0)

      const jobResponse = await assembleStoryboard({
        storyboard: {
          scenes,
          meta: {
            durationSec: totalDuration,
            scenesCount: scenes.length,
            brand: locationState?.meta?.brand,
            product: locationState?.meta?.product,
          },
        },
        outputFormat: 'mp4',
        aspectRatio: '9:16',
        includeAudio: true,
      })

      const { jobId } = jobResponse
      if (!jobId) {
        throw new Error('No jobId returned from assembly endpoint')
      }

      console.log('[performExport] Assembly job started:', jobId)
      setExportingStatus('Processing clips...')

      // Poll until completed or failed (max ~5 minutes: 60 attempts × 5s)
      const MAX_POLLS = 60
      const POLL_INTERVAL_MS = 5000
      let response: StoryboardAssemblyResponse | null = null

      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS))
        const status = await getAssemblyJobStatus(jobId)
        console.log(`[performExport] Poll ${i + 1}: status=${status.status}`)

        if (i === 2) setExportingStatus('Assembling video...')
        if (i === 6) setExportingStatus('Finalizing...')

        if (status.status === 'completed' || status.status === 'failed') {
          response = status
          break
        }
      }

      if (!response) {
        throw new Error('Assembly timed out. The video may still be processing — please try again shortly.')
      }

      if (response.success) {
        setExportResult(response)
        setShowExportResult(true)
      } else {
        setExportError(response.errorMessage || 'Export failed')
      }
    } catch (err) {
      console.error('[StoryboardDetail] Export failed:', err)
      setExportError(err instanceof Error ? err.message : 'Failed to export video')
    } finally {
      setIsExporting(false)
    }
  }

  const handleDownloadExportedVideo = () => {
    if (exportResult?.downloadUrl) {
      window.open(exportResult.downloadUrl, '_blank')
    }
  }

  const performExportClips = async () => {
    const framesWithVideo = benchmarkFrames.filter(f => f.sourceVideoId)
    if (framesWithVideo.length === 0) {
      setExportError('No clips assigned. Select footage for your scenes first.')
      return
    }

    setIsExportingClips(true)
    setExportError(null)

    try {
      const zip = new JSZip()

      // Build the storyboard text summary
      const lines: string[] = [
        `Storyboard: ${storyboardName}`,
        `Exported: ${new Date().toISOString()}`,
        `Total scenes: ${benchmarkFrames.length}`,
        '',
        '='.repeat(60),
        '',
      ]

      for (let i = 0; i < benchmarkFrames.length; i++) {
        const frame = benchmarkFrames[i]
        lines.push(`SCENE ${i + 1}: ${frame.label}`)
        lines.push(`  Time: ${frame.timeStart} → ${frame.timeEnd}`)
        if (frame.clipStartSec !== undefined || frame.clipEndSec !== undefined) {
          lines.push(`  Clip trim: ${frame.clipStartSec ?? 0}s → ${frame.clipEndSec ?? '?'}s`)
        }
        if (frame.textOverlay) lines.push(`  Text overlay: ${frame.textOverlay}`)
        if (frame.audioText) lines.push(`  Audio/VO: ${frame.audioText}`)
        if (frame.description) lines.push(`  Description: ${frame.description}`)
        if (frame.sourceVideoId) lines.push(`  Video ID: ${frame.sourceVideoId}`)
        lines.push('')
      }

      zip.file('storyboard.txt', lines.join('\n'))

      // Download each clip and add to ZIP
      const uniqueVideoIds = [...new Set(framesWithVideo.map(f => f.sourceVideoId!))]
      const videoUrlCache = new Map<string, string>()

      for (const videoId of uniqueVideoIds) {
        try {
          const detail = await getCompanyVideo(videoId)
          const url = detail.cleanVersionUrl || detail.downloadUrl
          if (url) videoUrlCache.set(videoId, url)
        } catch (err) {
          console.warn(`[ExportClips] Failed to get URL for ${videoId}:`, err)
        }
      }

      for (let i = 0; i < benchmarkFrames.length; i++) {
        const frame = benchmarkFrames[i]
        if (!frame.sourceVideoId) continue

        const url = videoUrlCache.get(frame.sourceVideoId)
        if (!url) continue

        const sceneNum = String(i + 1).padStart(2, '0')
        const safeLabel = frame.label.replace(/[^a-zA-Z0-9]/g, '_')
        const fileName = `scene_${sceneNum}_${safeLabel}.mp4`

        try {
          const resp = await fetch(url)
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
          const blob = await resp.blob()
          zip.file(fileName, blob)
        } catch (err) {
          console.warn(`[ExportClips] Failed to download clip for scene ${i + 1}:`, err)
        }
      }

      const blob = await zip.generateAsync({ type: 'blob' })

      const safeName = storyboardName.replace(/[^a-zA-Z0-9_\- ]/g, '').replace(/\s+/g, '_')
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${safeName}_clips.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(a.href)
    } catch (err) {
      console.error('[ExportClips] Failed:', err)
      setExportError(err instanceof Error ? err.message : 'Failed to export clips')
    } finally {
      setIsExportingClips(false)
    }
  }

  // Memoize currentFootage to prevent unnecessary re-renders and useEffect triggers in child components
  const currentFootage = useMemo(() => {
    if (!selectedFrame) return null
    return {
      id: selectedFrame.sourceVideoId || selectedFrame.id,
      filename: `Frame ${selectedFrame.timeStart}`,
      thumbnail: selectedFrame.image,
      duration: `${selectedFrame.timeStart} - ${selectedFrame.timeEnd}`,
      description: selectedFrame.description || '',
    }
  }, [
    selectedFrame?.id,
    selectedFrame?.sourceVideoId,
    selectedFrame?.timeStart,
    selectedFrame?.image,
    selectedFrame?.description,
  ])

  const copyLabel =
    copyStatus === 'success' ? 'Copied' : copyStatus === 'error' ? 'Copy failed' : 'Copy Link'

  // Loading state
  if (!isNewStoryboard && isLoadingStoryboard) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-tf-text-muted">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading storyboard...</span>
        </div>
      </div>
    )
  }

  // Save status indicator component
  const SaveStatusIndicator = () => {
    if (saveStatus === 'saving') {
      return (
        <div className="flex items-center gap-1.5 text-sm text-tf-text-muted">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>Saving...</span>
        </div>
      )
    }
    if (saveStatus === 'saved') {
      return (
        <div className="flex items-center gap-1.5 text-sm text-green-600">
          <Check className="w-3.5 h-3.5" />
          <span>Saved</span>
        </div>
      )
    }
    if (saveStatus === 'error') {
      return (
        <div className="flex items-center gap-1.5 text-sm text-red-600">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>Save failed</span>
        </div>
      )
    }
    if (hasUnsavedChanges) {
      return (
        <div className="flex items-center gap-1.5 text-sm text-tf-text-muted">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          <span>Unsaved changes</span>
        </div>
      )
    }
    return null
  }

  return (
    <div className="min-h-full flex flex-col">
      <StoryboardHeader
        storyboardName={storyboardName}
        onStoryboardNameChange={handleStoryboardNameChange}
        onRegenerate={handleRegenerate}
        onCopyLink={handleCopyLink}
        onExportVideo={handleExportVideoClick}
        onExportClips={performExportClips}
        copyLabel={copyLabel}
        saveStatusIndicator={<SaveStatusIndicator />}
        isExporting={isExporting}
        exportingStatus={exportingStatus}
        exportDisabled={benchmarkFrames.length === 0}
        isExportingClips={isExportingClips}
      />

      <div className="p-6">
        <div className="max-w-[1600px] mx-auto">
          <StoryboardComparison
            sourceFrames={sourceFrames}
            benchmarkFrames={benchmarkFrames}
            isAdaptingCopy={isAdaptingCopy}
            onBenchmarkFrameClick={handleBenchmarkFrameClick}
            onBenchmarkFramePreview={handleBenchmarkFramePreview}
            onBenchmarkRegenerate={handleBenchmarkRegenerate}
            onBenchmarkRemove={handleBenchmarkRemove}
            onBenchmarkUpdate={handleBenchmarkUpdate}
            onRegenerateSection={handleRegenerateSection}
          />
        </div>
      </div>

      {currentFootage && (
        <FootageSelectionModal
          open={isFootageModalOpen}
          onClose={() => setIsFootageModalOpen(false)}
          currentFootage={currentFootage}
          sceneLabel={selectedFrame?.label}
          initialTab={footageModalInitialTab}
          footageRepository={mockFootageRepository}
          onConfirm={handleFootageConfirm}
          initialClipStartSec={selectedFrame?.clipStartSec}
          initialClipEndSec={selectedFrame?.clipEndSec}
          sceneDurationSec={
            selectedFrame
              ? parseTimeToSeconds(selectedFrame.timeEnd) - parseTimeToSeconds(selectedFrame.timeStart)
              : undefined
          }
          sceneDescription={selectedFrame?.description}
          sceneTextOverlay={selectedFrame?.textOverlay}
          sceneAudio={selectedFrame?.audioText}
          aiFootageTags={selectedFrame?.aiFootages?.[0]?.tags}
        />
      )}

      {/* Missing Clips Warning Modal */}
      {showMissingClipsWarning && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowMissingClipsWarning(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <Card
              className="w-full max-w-md"
              onClick={event => event.stopPropagation()}
            >
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-tf-text">
                    Missing Footage
                  </h3>
                </div>
                <p className="text-sm text-tf-text-muted">
                  {missingClipsCount} scene{missingClipsCount > 1 ? 's' : ''} in your storyboard {missingClipsCount > 1 ? 'are' : 'is'} missing footage.
                  The export will use placeholder clips for these scenes.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800 font-medium">
                    Scenes without assigned video:
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {benchmarkFrames
                      .map((f, index) => ({ frame: f, sceneNum: index + 1 }))
                      .filter(({ frame }) => !frame.sourceVideoId)
                      .map(({ frame, sceneNum }) => (
                        <span
                          key={frame.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-xs text-amber-700"
                        >
                          <Film className="w-3 h-3" />
                          Scene {sceneNum}: {frame.label}
                        </span>
                      ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-tf-border">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowMissingClipsWarning(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={performExport}
                  >
                    Export Anyway
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </>
      )}

      {/* Export Result Modal */}
      {showExportResult && exportResult && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowExportResult(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <Card
              className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={event => event.stopPropagation()}
            >
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-tf-text">
                        Video Exported Successfully
                      </h3>
                      <p className="text-sm text-tf-text-muted">
                        {exportResult.sceneMatches?.filter(m => m.matched).length || 0} / {exportResult.sceneMatches?.length || 0} scenes matched
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Download className="w-4 h-4" />}
                    onClick={handleDownloadExportedVideo}
                  >
                    Download
                  </Button>
                </div>

                {/* Video Preview */}
                {exportResult.videoUrl && (
                  <div className="rounded-lg overflow-hidden border border-tf-border bg-black">
                    <video
                      src={exportResult.videoUrl}
                      controls
                      className="w-full max-h-[400px]"
                    />
                  </div>
                )}

                {/* Scene Matches */}
                {exportResult.sceneMatches && exportResult.sceneMatches.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide">
                      Scene Matches
                    </p>
                    <div className="grid gap-2 max-h-[200px] overflow-y-auto">
                      {exportResult.sceneMatches.map((match) => (
                        <div
                          key={match.sceneNumber}
                          className={`flex items-center justify-between rounded-lg border p-2 ${
                            match.matched
                              ? 'border-green-200 bg-green-50'
                              : 'border-orange-200 bg-orange-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium">
                              Scene {match.sceneNumber}
                            </span>
                            <span className="text-xs text-tf-text-muted">
                              {match.contentRole}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {match.videoTitle && (
                              <span className="text-xs text-tf-text-muted truncate max-w-[150px]">
                                {match.videoTitle}
                              </span>
                            )}
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                              match.matched
                                ? 'bg-green-100 text-green-700'
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {match.matched ? `${Math.round(match.matchScore * 100)}%` : 'Placeholder'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-2 border-t border-tf-border">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowExportResult(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </>
      )}

      {/* Export Error Toast */}
      {exportError && (
        <div className="fixed bottom-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Export Failed</p>
              <p className="text-xs text-red-600 mt-1">{exportError}</p>
            </div>
            <button
              onClick={() => setExportError(null)}
              className="text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default StoryboardDetail
